import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { ArrowRight, Ruler, Layers, Zap, Building2, Factory, TrendingUp, CheckCircle, Shield, Award, KeyRound } from "lucide-react";
import SEO from "@/components/SEO";

export default function CustomSolutionsPage() {
  const { t, isRTL } = useLanguage();

  const customizationFeatures = [
    t("customPage.customFeature1"),
    t("customPage.customFeature2"),
    t("customPage.customFeature3"),
    t("customPage.customFeature4"),
    t("customPage.customFeature5"),
    t("customPage.customFeature6")
  ];

  const acrylicOptions = [
    { type: t("customPage.acrylicOption1"), desc: t("customPage.acrylicOption1Desc") },
    { type: t("customPage.acrylicOption2"), desc: t("customPage.acrylicOption2Desc") },
    { type: t("customPage.acrylicOption3"), desc: t("customPage.acrylicOption3Desc") }
  ];

  const lowPowerBenefits = [
    t("customPage.lowPowerBenefit1"),
    t("customPage.lowPowerBenefit2"),
    t("customPage.lowPowerBenefit3"),
    t("customPage.lowPowerBenefit4")
  ];

  const integrationSupport = [
    { title: t("customPage.archCoord"), desc: t("customPage.archCoordDesc") },
    { title: t("customPage.structuralLoad"), desc: t("customPage.structuralLoadDesc") },
    { title: t("customPage.mepIntegration"), desc: t("customPage.mepIntegrationDesc") },
    { title: t("customPage.installSupport"), desc: t("customPage.installSupportDesc") }
  ];

  const productionCapabilities = [
    t("customPage.bulkFeature1"),
    t("customPage.bulkFeature2"),
    t("customPage.bulkFeature3"),
    t("customPage.bulkFeature4")
  ];

  return (
    <div data-testid="custom-solutions-page">
      <SEO 
        title="Custom Solutions for Developers | Balcony Plunge Pools & Spa Systems"
        description="Custom spa systems and Balcony Plunge Pools engineered for modern developments. Fully customizable dimensions, acrylic panel integration, and ultra-low power systems for high-rise installations."
        keywords="custom spa solutions, balcony plunge pools, developer spa systems, custom jacuzzi dimensions, acrylic panel spa, low power jacuzzi, bulk spa supply dubai"
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/custom-solutions` : "https://www.pruvajacuzzi.com/custom-solutions"}
        breadcrumbs={[
          { name: "Home", url: typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com" },
          { name: "Custom Solutions", url: typeof window !== 'undefined' ? `${window.location.origin}/custom-solutions` : "https://www.pruvajacuzzi.com/custom-solutions" }
        ]}
        service={{
          name: "Custom Spa & Jacuzzi Solutions for Developers",
          type: "Custom Manufacturing and Installation",
          description: "Bespoke spa systems and Balcony Plunge Pools designed for residential developments, hospitality projects, and commercial properties in Dubai and UAE.",
          offerings: [
            "Custom dimensions and configurations",
            "Acrylic panel integration",
            "Ultra-low power systems (0.3 kW)",
            "Bulk production for developments",
            "Structural load consultation",
            "Full installation and commissioning"
          ]
        }}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-amber-400 font-medium mb-3">
                {t("customPage.heroLabel")}
              </p>
              <h1 data-testid="custom-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6">
                {t("customPage.heroTitle")}
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                {t("customPage.heroSubtitle")}
              </p>
              <p className="text-base text-slate-400 leading-relaxed mb-8">
                {t("customPage.heroDesc")}
              </p>
              <div className={`flex flex-wrap gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link to="/contact" className="bg-white text-slate-800 px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-slate-100 transition-colors">
                  {t("customPage.discussProject")}
                </Link>
                <Link to="/custom-solutions/developer-services" className="border border-white/50 text-white px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-colors">
                  {t("customPage.developerServices")}
                </Link>
              </div>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src="/images/optimized/developer.webp"
                alt="Custom spa solutions for modern developments"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Civil Defense Approved Installation Banner */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-emerald-900 via-emerald-800 to-emerald-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                <Shield className="w-10 h-10 md:w-12 md:h-12 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <div className="flex-1 text-center lg:text-start">
              <span className="inline-block px-3 py-1 bg-white/20 text-white text-xs uppercase tracking-widest font-medium rounded-full mb-3">
                {t("home.civilDefenseBadge")}
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-3">
                {t("home.civilDefenseTitle")}
              </h2>
              <p className="text-emerald-100 text-base md:text-lg max-w-3xl">
                {t("home.civilDefenseText")}
              </p>
            </div>
            <div className="flex-shrink-0 hidden xl:block">
              <div className="flex flex-col gap-3">
                <div className={`flex items-center gap-2 text-white/90 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-5 h-5 text-emerald-300" />
                  {t("home.civilDefenseFeature1")}
                </div>
                <div className={`flex items-center gap-2 text-white/90 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-5 h-5 text-emerald-300" />
                  {t("home.civilDefenseFeature2")}
                </div>
                <div className={`flex items-center gap-2 text-white/90 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-5 h-5 text-emerald-300" />
                  {t("home.civilDefenseFeature3")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-6">
            {t("customPage.introText1")}
          </p>
          <p className="text-base text-slate-500 leading-relaxed">
            {t("customPage.introText2")}
          </p>
        </div>
      </section>

      {/* Project-Based Manufacturing */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className={`grid lg:grid-cols-2 gap-16 items-center ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div>
              <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Factory className="w-6 h-6 text-amber-700" />
                <p className="text-sm uppercase tracking-widest text-amber-700 font-medium">{t("customPage.projectMfg")}</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-6">
                {t("customPage.fullyCustomizable")}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                {t("customPage.projectMfgDesc1")}
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-8">
                {t("customPage.projectMfgDesc2")}
              </p>
              <div className="space-y-3">
                {customizationFeatures.map((feature, i) => (
                  <div key={i} className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-square overflow-hidden">
              <img 
                src="/images/optimized/custom-1.webp"
                alt="Custom spa manufacturing for developers"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Acrylic Panel Integration */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className={`${isRTL ? 'order-1 lg:order-2' : 'order-2 lg:order-1'} aspect-[4/3] overflow-hidden`}>
              <img 
                src="/images/optimized/custom-2.webp"
                alt="Acrylic panel spa with transparent sides"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className={`${isRTL ? 'order-2 lg:order-1' : 'order-1 lg:order-2'}`}>
              <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Layers className="w-6 h-6 text-amber-700" />
                <p className="text-sm uppercase tracking-widest text-amber-700 font-medium">{t("customPage.acrylicTitle")}</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-6">
                {t("customPage.acrylicStatement")}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {t("customPage.acrylicStatementDesc")}
              </p>
              <div className="grid gap-4 mb-8">
                {acrylicOptions.map((option, i) => (
                  <div key={i} className={`flex items-center gap-4 p-4 bg-slate-50 border border-slate-100 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <div className="w-2 h-2 bg-amber-700 rounded-full flex-shrink-0"></div>
                    <div>
                      <span className="font-medium text-slate-900">{option.type}</span>
                      <span className={`text-slate-500 ${isRTL ? 'mr-2' : 'ml-2'}`}>— {option.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-500">
                {t("customPage.acrylicSafetyNote")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ultra-Low Power Systems */}
      <section className="py-16 md:py-24 bg-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Zap className="w-6 h-6 text-amber-400" />
                <p className="text-sm uppercase tracking-widest text-amber-400 font-medium">{t("customPage.ultraLowPower")}</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-6">
                {t("customPage.balconySpa03kw")}
              </h2>
              <p className="text-base text-slate-300 leading-relaxed mb-6">
                {t("customPage.ultraLowPowerDesc1")}
              </p>
              <p className="text-base text-slate-400 leading-relaxed mb-8">
                {t("customPage.ultraLowPowerDesc2")}
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <div className="text-4xl font-semibold text-amber-400 mb-1">0.3 kW</div>
                  <div className="text-sm text-slate-400">{t("customPage.minimumPower")}</div>
                </div>
                <div>
                  <div className="text-4xl font-semibold text-amber-400 mb-1">70%</div>
                  <div className="text-sm text-slate-400">{t("customPage.energySavings")}</div>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-slate-300 font-medium mb-3">{t("customPage.idealFor")}</p>
                {lowPowerBenefits.map((benefit, i) => (
                  <div key={i} className={`flex items-center gap-2 text-sm text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Shield className="w-4 h-4 text-amber-400" />
                    {benefit}
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src="/images/optimized/custom-3.webp"
                alt="Ultra-low power balcony spa system"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Seamless Integration */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <div className={`flex items-center justify-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Building2 className="w-6 h-6 text-amber-700" />
              <p className="text-sm uppercase tracking-widest text-amber-700 font-medium">{t("customPage.seamlessIntegration")}</p>
            </div>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-4">
              {t("customPage.constructionSupport")}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {t("customPage.constructionSupportDesc")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrationSupport.map((item, i) => (
              <div key={i} className="p-6 bg-slate-50 border border-slate-100 hover:border-slate-300 transition-colors">
                <div className="text-4xl font-bold text-slate-200 mb-4">0{i + 1}</div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 mt-10 max-w-3xl mx-auto">
            {t("customPage.collaborativeApproach")}
          </p>
        </div>
      </section>

      {/* Scalable Production */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src="/images/optimized/custom-4.webp"
                alt="Scalable spa production for multi-unit developments"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Ruler className="w-6 h-6 text-amber-700" />
                <p className="text-sm uppercase tracking-widest text-amber-700 font-medium">{t("customPage.scalableProduction")}</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-6">
                {t("customPage.multiUnitCapability")}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {t("customPage.multiUnitDesc")}
              </p>
              <div className="space-y-3">
                {productionCapabilities.map((capability, i) => (
                  <div key={i} className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Property Value Section */}
      <section className="py-16 md:py-24 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <TrendingUp className="w-6 h-6 text-amber-700" />
                <p className="text-sm uppercase tracking-widest text-amber-700 font-medium">{t("customPage.investmentValue")}</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-6">
                {t("customPage.wellnessPropertyValue")}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                {t("customPage.propertyValueDesc1")}
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-8">
                {t("customPage.propertyValueDesc2")}
              </p>
              <div className={`p-6 bg-slate-50 ${isRTL ? 'border-r-4' : 'border-l-4'} border-amber-700`}>
                <p className="text-slate-700 italic">
                  "{t("customPage.propertyValueQuote")}"
                </p>
              </div>
            </div>
            <div className="aspect-square overflow-hidden">
              <img 
                src="/images/optimized/wellness.webp"
                alt="Luxury wellness feature adding property value"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TurnKey Solutions Banner */}
      <section className="py-12 bg-gradient-to-r from-amber-600 to-amber-700">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className={`flex flex-col md:flex-row items-center justify-between gap-6 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center">
                <KeyRound className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">{t("customPage.turnkeyBanner")}</h3>
                <p className="text-amber-100">{t("customPage.turnkeyBannerDesc")}</p>
              </div>
            </div>
            <Link to="/custom-solutions/developer-services" className={`bg-white text-amber-700 px-8 py-3 text-sm uppercase tracking-widest font-medium hover:bg-amber-50 transition-colors whitespace-nowrap inline-flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {t("customPage.learnMore")} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Quality Badges */}
          <div className={`flex flex-wrap justify-center gap-8 mb-12 pb-12 border-b border-slate-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-3 text-slate-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Factory className="w-6 h-6 text-amber-400" />
              <span>{t("customPage.madeInTurkey")}</span>
            </div>
            <div className={`flex items-center gap-3 text-slate-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Shield className="w-6 h-6 text-amber-400" />
              <span>{t("customPage.tenYearWarranty")}</span>
            </div>
            <div className={`flex items-center gap-3 text-slate-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Award className="w-6 h-6 text-amber-400" />
              <span>{t("customPage.ceCertified")}</span>
            </div>
          </div>
          
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-6">
              {t("customPage.discussDevelopment")}
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10">
              {t("customPage.discussDevelopmentDesc")}
            </p>
            <div className={`flex flex-wrap justify-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Link to="/contact" data-testid="custom-contact-cta"
                className={`bg-white text-slate-800 px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-slate-100 transition-colors inline-flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {t("customPage.requestConsultation")} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
              <a href="tel:+971542184086" className="border border-white/50 text-white px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-colors">
                {t("customPage.callNumber")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
